import { MOTION_PRESETS } from '../animation/motionPresets';
import * as A from '../domain/animationOps';
import { OpError } from '../domain/projectOps';
import * as S from '../domain/sceneOps';
import type { Animation, Channel, Project } from '../domain/schemas';
import type { Command } from './commands';

/** Explicit UI context used by animation prompts. */
export interface AnimationSelection {
  /** The animation shown on the timeline ("the current animation"). */
  animationId?: string | null;
  objectIds?: readonly string[];
  /** The playhead frame. */
  frame?: number;
}

export interface AnimationCommandResult {
  project: Project;
  message: string;
  select?: { animationId?: string | null };
}

const CHANNELS: Record<'position' | 'rotation' | 'scale' | 'opacity' | 'transform', Channel[]> = {
  position: ['x', 'y'],
  rotation: ['rotation'],
  scale: ['scaleX', 'scaleY'],
  opacity: ['opacity'],
  transform: ['x', 'y', 'rotation', 'scaleX', 'scaleY', 'opacity'],
};

function current(project: Project, selection: AnimationSelection | undefined): Animation {
  if (!selection?.animationId) throw new OpError('No animation is selected. Say “create a 4-second animation” first, or pick one on the timeline.');
  const a = project.animations.find((x) => x.id === selection.animationId);
  if (!a) throw new OpError('The selected animation no longer exists.');
  if (a.sceneId !== project.activeSceneId) throw new OpError(`“${a.name}” belongs to another scene. Switch scenes or create an animation here.`);
  return a;
}

function targets(project: Project, a: Animation, selection: AnimationSelection | undefined) {
  if (!selection?.objectIds?.length) throw new OpError('No object is selected. Select one on the canvas or in the layer list first.');
  const scene = S.sceneOf(project, a.sceneId);
  const objects = scene.objects.filter((o) => selection.objectIds!.includes(o.id));
  if (objects.length === 0) throw new OpError('The selected object is not in the current scene any more.');
  return objects;
}

const named = (names: string[]) => (names.length === 1 ? `“${names[0]}”` : `${names.length} objects`);

/** Applies an animation command. Throws OpError (nothing changes) on invalid context. */
export function applyAnimationCommand(project: Project, command: Command, selection: AnimationSelection | undefined): AnimationCommandResult | null {
  switch (command.type) {
    case 'anim.create': {
      const scene = S.activeSceneOf(project);
      const r = A.createAnimation(project, scene.id, { seconds: command.seconds, fps: command.fps });
      const a = A.animationOf(r.project, r.id);
      return { project: r.project, message: `Created “${a.name}” in “${scene.name}”: ${command.seconds} s at ${a.fps} fps (${a.durationFrames} frames, looping).`, select: { animationId: r.id } };
    }
    case 'anim.fps': {
      const a = current(project, selection);
      const next = A.setAnimationTiming(project, a.id, { fps: command.fps });
      return { project: next, message: `“${a.name}” now runs at ${command.fps} fps (${A.animationOf(next, a.id).durationFrames} frames; keyframes keep their timing).` };
    }
    case 'anim.duration': {
      const a = current(project, selection);
      return { project: A.setAnimationTiming(project, a.id, { seconds: command.seconds }), message: `“${a.name}” now lasts ${command.seconds} s.` };
    }
    case 'anim.loop': {
      const a = current(project, selection);
      const words = { once: 'plays once', loop: 'loops', pingpong: 'plays back and forth' };
      return { project: A.setAnimationTiming(project, a.id, { loop: command.mode }), message: `“${a.name}” ${words[command.mode]}.` };
    }
    case 'anim.key': {
      const a = current(project, selection);
      if (command.frame >= a.durationFrames) throw new OpError(`Frame ${command.frame} is past the end of “${a.name}” (last frame ${a.durationFrames - 1}).`);
      const objects = targets(project, a, selection);
      let p = project;
      for (const o of objects) for (const c of CHANNELS[command.what]) p = A.setKeyframe(p, a.id, o.id, c, command.frame, A.valueAt(p, a.id, o.id, c, command.frame));
      return { project: p, message: `Added ${command.what} keyframe${CHANNELS[command.what].length > 1 ? 's' : ''} at frame ${command.frame} for ${named(objects.map((o) => o.name))}.` };
    }
    case 'anim.move': {
      const a = current(project, selection);
      const objects = targets(project, a, selection);
      const scene = S.sceneOf(project, a.sceneId);
      const horizontal = command.direction === 'left' || command.direction === 'right';
      const distance = command.distance ?? Math.round((horizontal ? scene.width : scene.height) * 0.25);
      const sign = command.direction === 'left' || command.direction === 'up' ? -1 : 1;
      const end = command.seconds !== undefined ? Math.round(command.seconds * a.fps) : a.durationFrames - 1;
      if (end > a.durationFrames - 1) throw new OpError(`“${a.name}” is only ${(a.durationFrames / a.fps).toFixed(2)} s long. Make it longer first.`);
      if (end < 1) throw new OpError('The movement needs at least two frames.');
      const channel: Channel = horizontal ? 'x' : 'y';
      let p = project;
      for (const o of objects) {
        const start = A.valueAt(p, a.id, o.id, channel, 0);
        p = A.setKeyframe(p, a.id, o.id, channel, 0, start);
        p = A.setKeyframe(p, a.id, o.id, channel, end, start + sign * distance);
      }
      return { project: p, message: `${named(objects.map((o) => o.name))} now move${objects.length === 1 ? 's' : ''} ${distance} px ${command.direction} from frame 0 to ${end}.` };
    }
    case 'anim.rotate': {
      const a = current(project, selection);
      const objects = targets(project, a, selection);
      const f0 = Math.min(Math.max(0, selection?.frame ?? 0), a.durationFrames - 1);
      const f1 = f0 + Math.round(command.seconds * a.fps);
      if (f1 > a.durationFrames - 1) throw new OpError(`That needs frames ${f0}–${f1}, but “${a.name}” ends at frame ${a.durationFrames - 1}. Make the animation longer or move the playhead back.`);
      let p = project;
      for (const o of objects) {
        const start = A.valueAt(p, a.id, o.id, 'rotation', f0);
        p = A.setKeyframe(p, a.id, o.id, 'rotation', f0, start, 'linear');
        p = A.setKeyframe(p, a.id, o.id, 'rotation', f1, start + command.degrees);
      }
      return { project: p, message: `${named(objects.map((o) => o.name))} rotate${objects.length === 1 ? 's' : ''} ${command.degrees}° from frame ${f0} to ${f1}.` };
    }
    case 'anim.preset': {
      const a = current(project, selection);
      const objects = targets(project, a, selection);
      let p = project;
      for (const o of objects) p = A.applyMotionPreset(p, a.id, o.id, command.preset);
      return { project: p, message: `Applied “${MOTION_PRESETS[command.preset].label}” to ${named(objects.map((o) => o.name))} in “${a.name}” (editable keyframes).` };
    }
    default:
      return null;
  }
}
